// ---------------------------------------------------------------------------
// Prezio Onboard Widget — usePrefetchDsoOptions hook
// ---------------------------------------------------------------------------
// Prefetches dso_org options while the user is on a retailer step.
// This populates the optionsCache so the first DSO step loads instantly.
//
// Only dso_org is prefetched — later DSO steps (dso_main_tariff, dso_tariff,
// dso_properties) depend on collected DSO data that doesn't exist yet, so the
// backend would reject those requests with 400.
// ---------------------------------------------------------------------------

import { useEffect, useRef } from 'react';
import type { StepId } from '@/api/types';
import { useSessionActions, useSessionState } from '@/context/SessionContext';

/** The only DSO step whose dependencies are satisfied during the retailer flow. */
const DSO_PREFETCH_STEP: StepId = 'dso_org';

/**
 * While the current step is a retailer step, silently prefetch options for
 * `dso_org`. The cache prevents re-fetches on subsequent retailer steps.
 *
 * Call this from StepRouter — it has no UI, only side-effects.
 */
export function usePrefetchDsoOptions(currentStep: StepId | null): void {
  const { fetchOptions } = useSessionActions();
  const { optionsCache } = useSessionState();

  // Use a ref to read the cache without re-triggering the effect when it updates
  // (same pattern as useStepOptions).
  const optionsCacheRef = useRef(optionsCache);
  optionsCacheRef.current = optionsCache;

  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!currentStep?.startsWith('retailer_')) return;
    if (optionsCacheRef.current.has(DSO_PREFETCH_STEP) || attemptedRef.current) return;

    attemptedRef.current = true;

    // Fire-and-forget — errors are silently swallowed since the step
    // will retry when the user actually reaches it.
    void fetchOptions(DSO_PREFETCH_STEP).catch(() => {
      // Reset so the normal fetch path retries when the user arrives.
      attemptedRef.current = false;
    });
  }, [currentStep, fetchOptions]);
}
